<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { PageName } from '@/api/tracking';
import { DgrIcon, DgrLoading } from '@stockmarkteam/donguri-ui';
import SurveyContentRenderer from '@/components/survey/generate-content/survey-content-renderer.vue';
import {
  FeedSummaryProcessedResponse,
  FeedSummaryResponse,
  SummarizableFeature,
} from '@/types';
import { splitPartsIntoSections } from '@/utils/survey/feedSummarySections';
import { useFeedSummaryActions } from './composables/use-feed-summary-actions';
import { useFeedSummaryCollapse } from './composables/use-feed-summary-collapse';
import { useFeedSummaryContent } from './composables/use-feed-summary-content';
import FeedSummaryFeedback from './feed-summary-feedback-next.vue';

const props = defineProps<{
  feedSummary: FeedSummaryResponse;
  feature: SummarizableFeature;
  pageName: PageName;
  themeId?: number;
  suffix?: string;
  showMessageWhenClosed?: boolean;
  autoOpenWhenGenerated?: boolean;
}>();

const isProcessed = computed(
  () => props.feedSummary.generation_status === 'processed',
);
const isStale = computed(
  () =>
    isProcessed.value &&
    (props.feedSummary as FeedSummaryProcessedResponse).freshness === 'stale',
);

const { summaryFeature, contentParts, status, summaryInfo } =
  useFeedSummaryContent(props);

const sectionedContent = computed(() =>
  splitPartsIntoSections(contentParts.value),
);
const sections = computed(() => sectionedContent.value.sections);

const activeSectionIndex = ref(0);
watch(sections, () => (activeSectionIndex.value = 0));
const activeSection = computed(
  () =>
    sections.value[activeSectionIndex.value] as
      | (typeof sections.value)[number]
      | undefined,
);

const {
  isClose,
  hasOpened,
  displayIsInProgress,
  progressBarWidth,
  toggleClose,
  closeTransitionEnd,
} = useFeedSummaryCollapse(props, status, summaryInfo);

const showsClosedMessage = computed(
  () =>
    props.showMessageWhenClosed === true &&
    isProcessed.value &&
    (isClose.value || status.value.isInProgress),
);
const showsSummary = computed(
  () => !(props.showMessageWhenClosed === true && isClose.value),
);

const { hasConvertedSurvey, convertToSummary, copySummary } =
  useFeedSummaryActions(props, summaryFeature, summaryInfo);
</script>
<template>
  <div class="feed-summary" :class="{ 'fixed-size': showMessageWhenClosed }">
    <div class="title">
      <button
        class="toggle-button c-title-bold--m"
        :aria-expanded="!isClose"
        @click="toggleClose"
      >
        <span class="title-detail">
          主要トピック
          <span class="suffix c-text-regular--s">
            <DgrIcon
              name="info"
              size="small"
              :keep-fill="false"
              v-tooltip.bottom="{
                content:
                  'あなたの関心に合わせて最新情報を、AIが注目トピックごとにまとめます。',
                theme: 'small',
              }"
            />
            {{ suffix ?? '' }}
          </span>
        </span>
        <DgrIcon
          v-if="!status.isInProgress"
          name="chevron-up"
          size="small"
          class="toggle-icon"
          :class="{ close: isClose }"
          :keep-fill="false"
        />
      </button>
    </div>
    <div
      class="container"
      :class="{
        close: isClose && !showMessageWhenClosed,
        'has-opened': hasOpened,
      }"
      @transitionend="closeTransitionEnd"
    >
      <div class="inner-container">
        <div v-if="showsClosedMessage" class="closed-message c-text-regular--m">
          <template v-if="isStale">
            最新の情報をもとに、おすすめ情報を更新できます。<span
              class="c-title-bold--m"
              >「最新の関心で再生成」</span
            >から、ニュース・論文・特許・レポートの<span class="c-title-bold--m"
              >主要トピック</span
            >をまとめて確認できます。
          </template>
          <template v-else-if="isProcessed">
            あなたの関心に合わせて集めたニュース・論文・特許・レポートなどを<span
              class="c-title-bold--m"
              >AIが毎日主要トピックごとに要約</span
            >します。記事を1件ずつ読まなくても、<span class="c-title-bold--m"
              >その日の全体像を素早く把握</span
            >できます。
          </template>
        </div>
        <div v-if="status.isInProgress" class="loading-container">
          <div class="c-text-regular--m">
            あなたの関心に合わせた情報を、<span class="c-title-bold--m">AI</span
            >が<span class="c-title-bold--m">主要トピック</span
            >ごとに整理しています。
          </div>
          <div class="loading-header">
            <DgrLoading size="small" />
            <span class="c-text-regular--m">主要トピックを作成しています</span>
          </div>
          <div class="progress-bar">
            <div class="progress-bar-fill" />
          </div>
        </div>
        <div
          v-if="showsSummary"
          class="summary-container"
          :class="{ close: displayIsInProgress }"
        >
          <div class="summary-inner">
            <div class="summary">
              <div class="summary-tabs">
                <div v-if="sections.length > 0" class="tab-list" role="tablist">
                  <div
                    v-for="(section, i) in sections"
                    :key="i"
                    type="button"
                    role="tab"
                    class="tab c-title-bold--s"
                    :class="{ active: i === activeSectionIndex }"
                    :aria-selected="i === activeSectionIndex"
                    :aria-controls="`feed-summary-panel-${i}`"
                    @click="activeSectionIndex = i"
                  >
                    {{ section.title }}
                  </div>
                </div>
                <div
                  v-if="activeSection"
                  :id="`feed-summary-panel-${activeSectionIndex}`"
                  role="tabpanel"
                  class="tab-content"
                >
                  <div class="c-title-bold--m">
                    {{ sections[activeSectionIndex].title }}
                  </div>
                  <SurveyContentRenderer
                    :content-parts="activeSection.parts"
                    :status
                    :enable-animation="false"
                    :feature
                    :page-name
                    :summary-info="summaryInfo"
                  />
                </div>
              </div>
              <div class="actions">
                <FeedSummaryFeedback
                  :summary-info="summaryInfo"
                  :feature="summaryFeature"
                  :page-name
                />
                <div class="action-buttons">
                  <button
                    class="action c-title-bold--s c-btn c-btn--small c-btn--auto"
                    @click="copySummary"
                  >
                    <DgrIcon name="copy" size="small" :keep-fill="false" />
                    コピー
                  </button>
                  <button
                    class="action c-title-bold--s c-btn c-btn--small c-btn--auto"
                    @click="convertToSummary"
                  >
                    <DgrIcon
                      :name="hasConvertedSurvey ? 'thread' : 'plus'"
                      size="small"
                      :keep-fill="false"
                    />
                    {{
                      hasConvertedSurvey
                        ? 'スレッドを確認'
                        : 'スレッドを作成する'
                    }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.feed-summary {
  background: #ffffff;
  border: 1px solid $color-gray400;
  border-radius: 4px;

  &.fixed-size {
    box-sizing: border-box;
    height: 100%;
    display: flex;
    flex-direction: column;

    .inner-container,
    .summary-inner,
    .summary {
      display: flex;
      flex-direction: column;
    }

    .summary-container,
    .summary,
    .summary-tabs {
      flex: 1;
    }

    .container,
    .inner-container,
    .summary-container,
    .summary-inner,
    .summary,
    .summary-tabs {
      min-height: 0;
    }

    .tab-content {
      height: auto;
    }
  }

  .title {
    padding: 12px 16px;

    .title-detail {
      display: flex;
      align-items: center;
      gap: 8px;

      .suffix {
        display: flex;
        align-items: center;
        gap: 8px;
        color: $color-gray800;
      }
    }
    .toggle-button {
      display: flex;
      align-items: center;
      justify-content: space-between;
      background: transparent;
      border: none;
      padding: 0;
      width: 100%;
      height: auto;
      cursor: pointer;
    }

    .toggle-icon {
      color: $color-gray800;
      transition: transform 0.3s;
      &.close {
        transform: rotate(-180deg);
      }
    }
  }
}

.container {
  display: grid;
  grid-template-rows: 1fr;
  transition: grid-template-rows 0.3s ease;
  flex-grow: 1;

  /* 開いているときにoverflow: hiddenが残っていると
     マークのポップアップが見切れるので、開いていないときだけ
     overflow: hiddenを設定するようにする */
  &:not(.has-opened) {
    .inner-container {
      overflow: hidden;
    }
  }
  &.close {
    grid-template-rows: 0fr;
  }
}

.loading-container {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  border-top: 1px solid $color-gray400;

  .loading-header {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .progress-bar {
    width: 100%;
    height: 8px;
    background: $color-gray400;
    border-radius: 4px;
    overflow: hidden;

    .progress-bar-fill {
      width: v-bind(progressBarWidth);
      height: 100%;
      background: $color-green600;
      border-radius: 4px;
      transition: width 0.1s linear;
    }
  }
}

.summary-container {
  display: grid;
  grid-template-rows: 1fr;
  transition: grid-template-rows 0.5s ease;

  .summary-inner {
    overflow: hidden;
  }
  &.close {
    grid-template-rows: 0fr;
    padding-bottom: 16px;
  }
}

.summary {
  border-top: 1px solid $color-gray400;

  .summary-tabs {
    display: flex;

    .tab-list {
      display: flex;
      flex-direction: column;
      width: 214px;
      flex-shrink: 0;
      overflow-y: auto;
      border-right: 1px solid $color-gray400;
    }

    .tab {
      display: flex;
      justify-content: flex-start;
      align-content: baseline;
      padding: 12px 8px;
      border-left: 2px solid transparent;
      border-bottom: 1px solid $color-gray400;
      overflow: hidden;
      text-overflow: ellipsis;
      cursor: pointer;

      &.active {
        color: $color-green600;
        background: $color-green100;
        border-left: 2px solid $color-green600;
      }
    }

    .tab-content {
      box-sizing: border-box;
      min-height: 200px;
      padding: 12px 16px;
      --survey-text-font-size: 14px;
      overflow-y: auto;
    }
  }

  .actions {
    display: flex;
    justify-content: space-between;
    padding: 8px 12px;
    color: $color-gray800;
    border-top: 1px solid $color-gray400;

    .action-buttons {
      display: flex;

      .action {
        justify-content: flex-start;
        gap: 4px;
        color: $color-gray800;
        background: $color-white;
        border: none;

        &:hover {
          background: $color-gray200;
        }
      }
    }
  }
}

.closed-message {
  box-sizing: border-box;
  padding: 12px 16px;
  color: $color-gray1000;
  border-top: 1px solid $color-gray400;

  :deep(b) {
    font-weight: 700;
  }
}
</style>
